<template>
  <div class="message-input-bar" style="background: #f0f0f0; border-top: 1px solid #e0e0e0; flex-shrink: 0">
    <!-- File preview -->
    <div v-if="selectedFile" class="file-preview-bar px-3 pt-2 pb-1">
      <v-card outlined class="pa-2 d-flex align-center" style="border-radius: 8px; background: #fff">
        <v-img
          v-if="filePreviewUrl"
          :src="filePreviewUrl"
          max-width="72"
          max-height="72"
          class="rounded mr-2 flex-shrink-0"
          style="border: 1px solid #e0e0e0"
        />
        <v-icon v-else size="40" color="grey" class="mr-2">mdi-file-document-outline</v-icon>
        <div class="flex-grow-1 overflow-hidden">
          <div class="text-subtitle-2 text-truncate">{{ selectedFile.name }}</div>
          <div class="text-caption grey--text">{{ (selectedFile.size / 1024).toFixed(1) }} KB</div>
        </div>
        <v-btn icon small @click="clearFile">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </v-card>
    </div>

    <!-- Input row -->
    <div class="d-flex align-end pa-2 gap-2">
      <!-- Hidden file input -->
      <input
        type="file"
        ref="fileInput"
        style="display: none"
        accept="image/*,.pdf,.doc,.docx,.xlsx,.xls"
        @change="handleFileSelect"
      />

      <!-- Attach button -->
      <v-btn icon @click="$refs.fileInput.click()" style="margin-bottom: 4px; flex-shrink: 0">
        <v-icon color="grey darken-1">mdi-paperclip</v-icon>
      </v-btn>

      <!-- Text area -->
      <v-textarea
        v-model="messageText"
        outlined
        dense
        auto-grow
        rows="1"
        :max-rows="4"
        placeholder="Type a message…"
        hide-details
        background-color="white"
        style="border-radius: 22px; flex: 1"
        class="mx-1 message-textarea"
        @keydown.enter.exact.prevent="sendMessage"
        @input="handleTyping"
      />

      <!-- Send button -->
      <v-btn
        fab
        small
        depressed
        :color="canSend ? 'success' : 'grey lighten-2'"
        :loading="sending"
        :disabled="!canSend"
        style="margin-bottom: 2px; flex-shrink: 0"
        @click="sendMessage"
      >
        <v-icon :color="canSend ? 'white' : 'grey'" size="20">mdi-send</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageInput',

  props: {
    chat: { type: Object, required: true }
  },

  data() {
    return {
      messageText: '',
      selectedFile: null,
      filePreviewUrl: null,
      sending: false,
      typingTimeout: null,
      isTyping: false
    };
  },

  computed: {
    canSend() {
      return (this.messageText.trim() || this.selectedFile) && !this.sending;
    }
  },

  methods: {
    async sendMessage() {
      if (!this.canSend) return;
      this.sending = true;

      try {
        const formData = new FormData();
        if (this.messageText.trim()) {
          formData.append('message', this.messageText.trim());
        }
        formData.append('chat_id', this.chat.id);

        if (this.selectedFile) {
          formData.append('file_path', this.selectedFile, this.selectedFile.name);
          formData.append('filename', this.selectedFile.name);
        }

        if (this.chat.is_group) {
          formData.append('is_group', 'true');
          if (this.chat.store_id) formData.append('store_id', this.chat.store_id);
        } else {
          const other = this.chat.users?.find(u => u.id !== this.user.id);
          if (other) formData.append('user_id', other.id);
        }

        await this.$store.dispatch('chat/sendMessage', formData);
        this.messageText = '';
        this.clearFile();
        this.stopTyping();
      } catch {
        this.$toast.error('Failed to send message');
      } finally {
        this.sending = false;
      }
    },

    handleFileSelect(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        this.$toast.error('File size must be less than 10MB');
        return;
      }

      const allowed = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf', 'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!allowed.includes(file.type)) {
        this.$toast.error('Invalid file type');
        return;
      }

      this.selectedFile = file;

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => { this.filePreviewUrl = ev.target.result; };
        reader.readAsDataURL(file);
      } else {
        this.filePreviewUrl = null;
      }
    },

    clearFile() {
      this.selectedFile = null;
      this.filePreviewUrl = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    handleTyping() {
      if (!this.isTyping) {
        this.isTyping = true;
        this.broadcastTyping();
      }
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => this.stopTyping(), 2000);
    },

    broadcastTyping() {
      if (!this.$echo) return;
      try {
        const channel = this.chat.is_group
          ? this.$echo.join(`room.${this.chat.id}`)
          : this.$echo.private(`chat.${this.chat.id}`);
        channel.whisper('typing', { userId: this.user.id });
      } catch (e) {
        console.warn('Failed to broadcast typing:', e);
      }
    },

    stopTyping() {
      if (!this.isTyping) return;
      this.isTyping = false;
      try {
        const channel = this.chat.is_group
          ? this.$echo.join(`room.${this.chat.id}`)
          : this.$echo.private(`chat.${this.chat.id}`);
        channel.whisper('stopped-typing', { userId: this.user.id });
      } catch (e) {
        console.warn('Failed to broadcast stopped-typing:', e);
      }
    }
  },

  beforeDestroy() {
    this.stopTyping();
    clearTimeout(this.typingTimeout);
  }
};
</script>

<style scoped>
.message-input-bar {
  position: relative;
}

.message-textarea :deep(.v-input__slot) {
  border-radius: 22px !important;
  min-height: 40px !important;
}

.message-textarea :deep(textarea) {
  padding: 8px 4px !important;
  line-height: 1.5 !important;
}
</style>
